import { forwardRef, useEffect, useState } from 'react';
import { displayNumbers } from '../../../../variables/numbersFunctions';
import { frenchMonth } from '../../../../variables/functions';
import { exportBordereauExcel, moisPeriodeLabel } from './bordereauExcel';
import CompteDeCessionParCategorieParCompagnie from '../documents/CompteDeCessionParCategorieParCompagnie';
import { getCompagnieLogo } from '../../../../hooks/useFilesHooks';

const CompteDeCession = forwardRef(({
    validationState,
    cessionExistante,
    totals,
    onValidate,
    onPrint,
    onRecalculer,
    recalculLoading,
    isValidated,
    isDisabled,
    loading,
    categorie,
    periode,
    compagnie,
    // variant="retro" : compte ALIGNÉ sur la rétrocession (/retrocessions) → émissions = cédé − prime
    // reportée, indemnités 15 %, sinistres, solde = émissions − sinistres − indemnités. On masque
    // annulations / recours / indemnités sur annulation (absents du compte de cession côté rétro).
    variant
}, ref) => {
    const isRetro = variant === 'retro';

    const [isPrinting, setIsPrinting] = useState(false);
    const [logoPreviewUrl, setLogoPreviewUrl] = useState(false);

    useEffect(() => {
        if (compagnie?.logo) {
            getCompagnieLogo(compagnie.code)
                .then(async (response) => {
                    const imageBytes = await response.arrayBuffer();
                    var blob = new Blob([imageBytes], { type: "image/png" });
                    var imageUrl = URL.createObjectURL(blob);
                    setLogoPreviewUrl(imageUrl);
                })
                .catch((error) => {
                    console.log(error);
                });
        }
    }, [compagnie]);

    const allBordereauxValidated =
        validationState.emissions.validated &&
        validationState.annulations.validated &&
        validationState.sinistresPayes.validated &&
        validationState.recoursEncaisses.validated;

    if (!allBordereauxValidated && !cessionExistante) {
        return (
            <div className="alert alert-warning" role="alert">
                <i className="tf-icons bx bx-error-circle me-2"></i>
                Veuillez valider tous les bordereaux avant d'accéder au compte de cession.
            </div>
        );
    }


    const handlePrint = () => {
        setIsPrinting(true);
        // Petit délai pour s'assurer que le DOM est mis à jour
        setTimeout(() => {
            onPrint();
            // Remettre l'état après l'impression
            setTimeout(() => setIsPrinting(false), 100);
        }, 100);
    };

    // Export Excel du compte (débit/crédit), aligné sur le tableau affiché.
    const handleExportExcel = () => {
        const t = (v) => Math.trunc(v || 0);
        // Toujours la PRIME NETTE recalculée (totals), y compris pour une cession validée —
        // et non la valeur historiquement sauvée en TTC.
        const totalEmissions = totals.totalEmissions;
        const rows = isRetro
            ? [
                ...(totals.reportee > 1
                    ? [
                        ['Prime nette cédée', '', t(totals.cede)],
                        ['Prime nette reportée (effet exercice futur)', t(totals.reportee), ''],
                    ]
                    : []),
                [totals.reportee > 1 ? 'Total des souscriptions à rétrocéder' : 'Montant total des souscriptions', '', t(totalEmissions)],
                ['Indemnités de gestion (15% x Prime nette)', t(totals.indemnitesEmissions), ''],
                ['Sinistres Payés (Montant sinistres payés)', t(totals.totalSinistres), ''],
                ['TOTAL', t(totals.debit), t(totals.credit)],
                [`Solde en faveur de ${totals.soldeEnFaveurDe || ''}`, '', t(totals.soldeMontant)],
            ]
            : [
                ['Montant total des souscriptions (Prime nette cédée)', '', t(totalEmissions)],
                ['Annulations et Ristournes (Primes négatives)', t(totals.totalAnnulations), ''],
                ['Indemnités de gestion (15% x Prime nette)', t(totals.indemnitesEmissions), ''],
                ['Indemnités de gestion sur annulation (15% x Primes négatives)', '', t(totals.indemnitesAnnulations)],
                ['Sinistres Payés (Montant sinistres payés)', t(totals.totalSinistres), ''],
                ['Recours encaissés (Montant recours encaissés)', '', t(totals.totalRecours)],
                ['TOTAL', t(totals.debit), t(totals.credit)],
                [`Solde en faveur de ${totals.soldeEnFaveurDe || ''}`, '', t(totals.soldeMontant)],
            ];
        exportBordereauExcel({
            titre: `COMPTE DE CESSION DU MOIS DE ${moisPeriodeLabel(periode).toUpperCase()}`,
            metaLines: [`Catégorie ${categorie?.codeCategorie || ''}`, `Compagnie : ${compagnie?.raisonSociale || ''}`],
            headers: ['Libellé', 'Débit', 'Crédit'],
            rows,
            nomFichier: `Compte_De_Cession_${categorie?.codeCategorie || ''}_${moisPeriodeLabel(periode)}`,
        });
    };

    return (
        <div>
            {/* Version affichage */}
            <div className="mb-4" style={{ padding: 25 }}>
                <div className="alert alert-info mt-3" role="alert" style={{ border: "1px" }}>
                    <i className="tf-icons bx bx-info-circle me-2"></i>
                    <strong>Solde {totals.solde > 0 ? 'DÉBITEUR' : 'CRÉDITEUR'}:</strong>
                    {/* Règle canonique (cf. assignation du chèque, CessionsPool.js) :
                        solde < 0 ⇒ EN FAVEUR DU POOL ⇒ la compagnie doit verser au Pool.
                        Les deux phrases étaient PERMUTÉES : l'encadré contredisait le badge
                        CRÉDITEUR/DÉBITEUR et le « Solde en faveur de » (tous deux corrects). */}
                    {totals.solde > 0
                        ? ' Le Pool TPV doit verser ce montant à la compagnie.'
                        : ' La compagnie doit verser ce montant au Pool TPV.'
                    }
                </div>
                <div className="table-responsive">
                    <table className="table table-bordered">
                        <thead className="table-light">
                            <tr>
                                <th style={{ width: '60%' }}>LIBELLÉ</th>
                                <th className="text-end" style={{ width: '20%' }}>DÉBIT</th>
                                <th className="text-end" style={{ width: '20%' }}>CRÉDIT</th>
                            </tr>
                        </thead>
                        <tbody>
                            {/* En mode rétro, si une prime reportée existe, on détaille cédé → reportée → net. */}
                            {isRetro && totals.reportee > 1 && (
                                <>
                                    <tr>
                                        <td><strong>Prime nette cédée</strong></td>
                                        <td className="text-end"></td>
                                        <td className="text-end">{displayNumbers(totals.cede)} F CFA</td>
                                    </tr>
                                    <tr>
                                        <td><strong>Prime nette reportée</strong> (effet d'exercice futur, non répartie)</td>
                                        <td className="text-end">{displayNumbers(totals.reportee)} F CFA</td>
                                        <td className="text-end"></td>
                                    </tr>
                                </>
                            )}
                            <tr>
                                <td><strong>{isRetro && totals.reportee > 1 ? 'Total des souscriptions à rétrocéder' : 'Montant total des souscriptions'}</strong> (Prime nette cédée)</td>
                                <td className="text-end"></td>
                                <td className="text-end">{displayNumbers(totals.totalEmissions)} F CFA</td>
                            </tr>
                            {!isRetro && (
                                <tr>
                                    <td><strong>Annulations et Ristournes</strong> (Primes négatives)</td>
                                    <td className="text-end">{displayNumbers(totals.totalAnnulations)} F CFA</td>
                                    <td className="text-end"></td>
                                </tr>
                            )}
                            <tr>
                                <td><strong>Indemnités de gestion</strong> (15% x Prime nette)</td>
                                <td className="text-end">{displayNumbers(totals.indemnitesEmissions)} F CFA</td>
                                <td className="text-end"></td>
                            </tr>
                            {!isRetro && (
                                <tr>
                                    <td><strong>Indemnités de gestion sur annulation</strong> (15% x Primes négatives)</td>
                                    <td className="text-end"></td>
                                    <td className="text-end">{displayNumbers(totals.indemnitesAnnulations)} F CFA</td>
                                </tr>
                            )}
                            <tr>
                                <td><strong>Sinistres Payés</strong> (Montant sinistres payés)</td>
                                <td className="text-end">{displayNumbers(totals.totalSinistres)} F CFA</td>
                                <td className="text-end"></td>
                            </tr>
                            {!isRetro && (
                                <tr>
                                    <td><strong>Recours encaissés</strong> (Montant recours encaissés)</td>
                                    <td className="text-end"></td>
                                    <td className="text-end">{displayNumbers(totals.totalRecours)} F CFA</td>
                                </tr>
                            )}
                            <tr className="table-active">
                                <td><strong>TOTAL</strong></td>
                                <td className="text-end"><strong>{displayNumbers(totals.debit)} F CFA</strong></td>
                                <td className="text-end"><strong>{displayNumbers(totals.credit)} F CFA</strong></td>
                            </tr>
                            {totals?.solde > 0 ? (
                                <tr className={totals.solde > 0 ? 'table-success' : 'table-warning'}>
                                    <td colSpan={2}><strong>Solde en faveur de {totals.soldeEnFaveurDe}</strong></td>
                                    <td className="text-end"><strong>{displayNumbers(totals.soldeMontant)} F CFA</strong></td>
                                </tr>
                            ) : (
                                <tr className={totals.solde > 0 ? 'table-success' : 'table-warning'}>
                                    <td><strong>Solde en faveur de {totals.soldeEnFaveurDe}</strong></td>
                                    <td className="text-end"><strong>{displayNumbers(totals.soldeMontant)} F CFA</strong></td>
                                    <td></td>
                                </tr>
                            )}

                            <tr className="table-info">
                                <td><strong>BALANCE</strong></td>
                                <td className="text-end"><strong>{displayNumbers(totals.solde > 0 ? totals.debit : totals.credit)} F CFA</strong></td>
                                <td className="text-end"><strong>{displayNumbers(totals.solde > 0 ? totals.debit : totals.credit)} F CFA</strong></td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <div className="mt-3 d-flex gap-2">
                    <button
                        type="button"
                        onClick={onValidate}
                        disabled={isDisabled || isValidated || loading}
                        className={isValidated ? "btn btn-success" : "btn btn-primary btn-lg"}
                    >
                        {loading && (
                            <span className="spinner-grow spinner-grow-sm me-1" role="status" aria-hidden="true"></span>
                        )}
                        <i className="tf-icons bx bxs-badge-check bx-18px me-2"></i>
                        <span className="align-middle">
                            {loading ? 'Validation en cours...' : isValidated
                                ? `Cession ${categorie?.codeCategorie} de ${periode ? frenchMonth(periode.split("-")[1]) + " " + periode.split("-")[0] : ''} validée`
                                : `Valider la cession ${categorie?.codeCategorie} de ${periode ? frenchMonth(periode.split("-")[1]) + " " + periode.split("-")[0] : ''}`
                            }
                        </span>
                    </button>

                    <button
                        type="button"
                        onClick={handlePrint}
                        className="btn btn-label-pinterest btn-lg"
                    >
                        <i className="tf-icons bx bxs-file-pdf bx-18px me-2"></i>
                        Imprimer (PDF)
                    </button>

                    <button
                        type="button"
                        onClick={handleExportExcel}
                        className="btn btn-label-success btn-lg"
                    >
                        <i className="tf-icons bx bxs-file-export bx-18px me-2"></i>
                        Exporter Excel
                    </button>

                    {/* Re-valider en prime nette : uniquement pour une cession DÉJÀ validée,
                        afin de corriger une ancienne valeur enregistrée en TTC. */}
                    {(isValidated || cessionExistante) && typeof onRecalculer === 'function' && (
                        <button
                            type="button"
                            onClick={onRecalculer}
                            disabled={recalculLoading}
                            className="btn btn-label-warning btn-lg"
                            title="Recalcule les totaux enregistrés sur la prime nette"
                        >
                            {recalculLoading && (
                                <span className="spinner-grow spinner-grow-sm me-1" role="status" aria-hidden="true"></span>
                            )}
                            <i className="tf-icons bx bx-refresh bx-18px me-2"></i>
                            Recalculer (prime nette)
                        </button>
                    )}
                </div>
            </div>

            {/* Version impression (cachée) */}
            <div ref={ref}
                style={{
                    position: isPrinting ? 'relative' : 'absolute',
                    left: isPrinting ? 'auto' : '-9999px',
                    visibility: isPrinting ? 'visible' : 'hidden'
                }}
            >
                <style>
                    {`
                        @media print {
                            @page {
                                size: portrait;
                                margin: 20mm;
                            }
                            body {
                                print-color-adjust: exact;
                                -webkit-print-color-adjust: exact;
                            }
                            
                            /* Force bold text */
                            strong, b, th, .fw-bold {
                                font-weight: bold !important;
                            }
                            
                            /* Force bold on specific elements */
                            td[style*="fontWeight: bold"],
                            td[style*="fontWeight:bold"],
                            p[style*="fontWeight: bold"],
                            p[style*="fontWeight:bold"],
                            th[style*="fontWeight: bold"],
                            th[style*="fontWeight:bold"] {
                                font-weight: bold !important;
                            }
                        }
                    `}
                </style>
                <CompteDeCessionParCategorieParCompagnie
                    // souscriptions={souscriptions}
                    // sinistres={sinistres}
                    categorie={categorie}
                    compagnie={compagnie}
                    periode={periode}
                    totals={totals}
                    logoPreviewUrl={logoPreviewUrl}
                />
            </div>
        </div>
    );
});

CompteDeCession.displayName = 'CompteDeCession';

export default CompteDeCession;